import { toDate, toDateStr } from "~/app/core/model/date-str";
import { StorageService } from "~/app/core/storage.service";
import { LocationService } from "~/app/core/location.service";
import { NotificationService } from "~/app/core/notification.service";
import { SettingsService } from "~/app/core/settings.service";
import { OfferInfo } from "~/app/core/model/offer-info";
import { SAMPLE_OFFERS, SAMPLE_SPONSORS } from "~/app/core/model/sample-data";
import * as _ from "lodash";
import { InterfaceOfferService } from "~/app/core/offer-service/interface-offer-service";
import { SuccessResult } from "~/app/core/model/connect";
import { Settings } from "~/app/core/model/settings";
import { AlternateLocationsList } from "~/app/core/model/rpc/alternate-locations-list";
import * as Log from "~/app/core/log";
import { OfferSearch } from "~/app/core/model/offer-search";
import { OfferAvailableInfo } from "~/app/core/model/rpc/offer-available-info";
import { OfferFilterOptions } from "~/app/core/model/offer-filter-options";

export class SampleOfferService implements InterfaceOfferService {
    constructor(
        private readonly locationService: LocationService,
        private readonly notificationService: NotificationService,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService
    ) {
    }

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    initService() {
        const offerSearch = this.storageService.loadOfferSearch();
        if (!offerSearch) {
            this.storageService.storeOfferSearch({
                location: true,
                radius: 50
            });
        }
    }

    nearbyOffer(offerId: number, addressId: number) {
        // do nothing
    }

    seenOffer(offerId: number) {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.seenDate) {
            offerInfo.seenDate = toDateStr(this.settings.calculateCurrentTime());
            this.storageService.storeOfferInfo(offerInfo);
        }
    }

    watchedVideo(offerId: number): OfferInfo {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            if (!offerInfo.watchedVideo) {
                offerInfo.watchedVideo = toDateStr(this.settings.calculateCurrentTime());
                this.storageService.storeOfferInfo(offerInfo);
            }
        }
        return offerInfo;
    }

    generateOfferShare(offerId: number): Promise<{ url: string, image: string }> {
        return Promise.resolve({ url: "https://whutsfree.com", image: null });
    }

    revealOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.revealed) {
            offerInfo.revealed = true;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    repeatUnlockOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.repeatUnlocked) {
            offerInfo.repeatUnlocked = true;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    favoriteOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.favorite) {
            offerInfo.favorite = true;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    unfavoriteOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && offerInfo.favorite) {
            offerInfo.favorite = false;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    redeemOffer(offerId: number, addressId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            if (!offerInfo.redeemedDate) {
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        offerInfo.redeemedDate = toDateStr(this.settings.calculateCurrentTime());
                        if (offerInfo.bounceBackOfferId) {
                            const when: Date = this.settingsService.loadSettings().calculateBounceBack(toDate(offerInfo.redeemedDate), offerInfo.sample);
                            offerInfo.bouncebackDate = toDateStr(when);
                        }
                        this.storageService.storeOfferInfo(offerInfo);
                        resolve(offerInfo);
                    }, 2500);
                });
            } else {
                return Promise.resolve(offerInfo);
            }
        } else {
            return Promise.reject("Missing");
        }
    }

    isAvailableOffer(offerId: number): Promise<OfferAvailableInfo> {
        return Promise.resolve({available: true});
    }

    testResetOffers(): Promise<SuccessResult> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                this.storageService.resetOffers();
                resolve({ success: true });
            }, 2500);
        });
    }

    testResetOffer(offerId: number, resetRedeemed: boolean, resetRevealed: boolean, resetRepeatUnlocked: boolean, resetThumbsup: boolean, resetBounceback: boolean, resetVideo: boolean): Promise<OfferInfo> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const offerInfo = this.storageService.loadOfferInfo(offerId);
                if (offerInfo) {
                    if (resetRepeatUnlocked) {
                        offerInfo.repeatUnlocked = false;
                    }
                    if (resetRevealed) {
                        offerInfo.revealed = false;
                        resetRedeemed = true;
                        resetBounceback = true;
                    }
                    if (resetRedeemed) {
                        offerInfo.redeemedDate = null;
                        resetBounceback = true;
                    }
                    if (resetBounceback) {
                        resetThumbsup = true;
                        if (offerInfo.redeemedDate) {
                            if (offerInfo.bounceBackOfferId) {
                                const when: Date = this.settingsService.loadSettings().calculateBounceBack(this.settings.calculateCurrentTime(), offerInfo.sample);
                                offerInfo.bouncebackDate = toDateStr(when);
                            }
                        } else {
                            offerInfo.bouncebackDate = null;
                        }
                    }
                    if (resetThumbsup) {
                        offerInfo.thumbsDate = null;
                        offerInfo.thumbs = null;
                    }
                    if (resetVideo) {
                        offerInfo.watchedVideo = null;
                    }
                    this.storageService.storeOfferInfo(offerInfo);
                }
                resolve(offerInfo);
            }, 500);
        });
    }

    recordOfferExperience(offerId: number, thumbs: "up"|"down"|"skip"): SuccessResult {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            offerInfo.thumbs = thumbs;
            offerInfo.thumbsDate = toDateStr(this.settings.calculateCurrentTime());
            this.storageService.storeOfferInfo(offerInfo);
            return { success: true };
        } else {
            return { success: false };
        }
    }

    recordOfferAdditionalPurchase(offerId: number, yesNo: "yes"|"no"): SuccessResult {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            offerInfo.purchaseSurveyAnswer = yesNo;
            offerInfo.purchaseSurveyDate = toDateStr(this.settings.calculateCurrentTime());
            this.storageService.storeOfferInfo(offerInfo);
            return { success: true };
        } else {
            return { success: false };
        }
    }

    performSearch(): Promise<Array<OfferInfo>> {
        // clear out last search
        const offerList: Array<number> = [];
        const search = this.storageService.loadOfferSearch();
        this.storageService.storeOfferListSearch([]);

        Log.debug(Log.Tag.SampleOfferService, "search: ");
        Log.debug(Log.Tag.SampleOfferService, search);

        // perform new search
        return new Promise((resolve, reject) => {
            const offers: Array<OfferInfo> = [];
            if (search.location || search.zipCode !== '12345') {
                SAMPLE_OFFERS.forEach((offerInfo) => {
                    const offer = _.cloneDeep(offerInfo);
                    offers.push(offer);
                    this.storageService.storeOfferInfo(offer);
                    offerList.push(offer.id);
                });
            }
            this.storageService.storeOfferListSearch(offerList);
            this.storageService.storeSponsorInfo(SAMPLE_SPONSORS[0]);

            return resolve(offers);
        });
    }

    alternateLocations(search: OfferSearch, limit: number): Promise<AlternateLocationsList> {
        return Promise.resolve({
            locations: [
                { zip: "84601", label: "Utah County" },
                { zip: "92610", label: "Los Angeles" }
            ]
        });
    }

    loadFilterOptions(): OfferFilterOptions {

        const options: OfferFilterOptions = {};
        options.categories = [
            "Food",
            "Health & Beauty",
            "Education",
            "Arts & Entertainment",
            "Travel",
            "Pets",
            "Home Improvement",
            "Automotive",
            "Electronic",
            "Fitness",
            "Recreation"
        ];

        options.categoryTagSections = new Map();
        {
            const newTags = new Map();
            options.categoryTagSections.set("Food", newTags);
            {
                const tagList = [];
                tagList.push("Asian");
                tagList.push("Indian");
                tagList.push("Mediterranean");
                tagList.push("Middle Eastern");
                tagList.push("Italian");
                tagList.push("Mexican");
                tagList.push("American");
                tagList.push("Soul Food");
                tagList.push("Tapas");
                tagList.push("Healthy");
                tagList.push("Fast Food");
                tagList.push("Fusion");
                newTags.set("Cuisines", tagList);
            }
            {
                const tagList = [];
                tagList.push("Vegan");
                tagList.push("Vegetarian");
                tagList.push("Dairy Free");
                tagList.push("Gluten Free");
                tagList.push("Kosher");
                tagList.push("Halal");
                tagList.push("Keto");
                newTags.set("Dietary", tagList);
            }
            {
                const tagList = [];
                tagList.push("Breakfast");
                tagList.push("Brunch");
                tagList.push("Lunch");
                tagList.push("Dinner");
                tagList.push("Quick Bite / Grab & Go");
                tagList.push("Dessert");
                tagList.push("Coffee");
                newTags.set("Meal Type", tagList);
            }
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Health & Beauty", newTags);
            const tagList = [];
            tagList.push("Hair & Makeup Services");
            tagList.push("Mental Health");
            newTags.set("Health & Beauty", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Education", newTags);
            const tagList = [];
            tagList.push("For Adults");
            tagList.push("For Children");
            tagList.push("Tutors");
            tagList.push("Group Class");
            tagList.push("Online Class");
            newTags.set("Education", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Arts & Entertainment", newTags);
            const tagList = [];
            tagList.push("Music");
            tagList.push("Comedy");
            tagList.push("Cinema");
            tagList.push("Gallery / Museum");
            tagList.push("Performing Arts");
            tagList.push("Sports");
            tagList.push("Festival");
            newTags.set("Arts & Entertainment", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Travel", newTags);
            const tagList = [];
            tagList.push("Hotels");
            tagList.push("Transportation");
            tagList.push("Tours");
            newTags.set("Travel", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Pets", newTags);
            const tagList = [];
            tagList.push("Grooming");
            tagList.push("Boarding");
            tagList.push("Veterinarian");
            tagList.push("Pet Supplies");
            newTags.set("Pets", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Home", newTags);
            const tagList = [];
            tagList.push("Lawn / Garden");
            tagList.push("Electric");
            tagList.push("Home Organization / Interior Design");
            tagList.push("Real Estate");
            newTags.set("Home", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Automotive", newTags);
            const tagList = [];
            tagList.push("Wash / Cleaning");
            tagList.push("Repairs");
            tagList.push("Tires");
            tagList.push("Rentals");
            newTags.set("Automotive", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Electronic", newTags);
            const tagList = [];
            tagList.push("Cleaning");
            tagList.push("Repair");
            newTags.set("Electronic", tagList);
        }
        {
            const newTags = new Map();
            options.categoryTagSections.set("Fitness", newTags);
            const tagList = [];
            tagList.push("Kid Friendly");
            tagList.push("Group Class");
            tagList.push("Private Class");
            tagList.push("Outdoors");
            tagList.push("No Equipment Required");
            tagList.push("Beginner Level");
            tagList.push("Intermediate Level");
            tagList.push("Expert Level");
            newTags.set("Fitness", tagList);
        }

        return options;
    }
}
